<template>
    <FilterBase :title>
        <ThButton
            v-for="value in bounds"
            :key="value.id"
            v-qa="JSON.stringify(value)"
            type="solid"
            size="small"
            :disabled
            :selected="isSelected(value)"
            :class="getClasses(value)"
            @click="select(value)"
        >
            {{ getGemstoneTypeName(value) }}
        </ThButton>
    </FilterBase>
</template>

<script setup lang="ts">
import type { NonSkuGemstoneType } from '@/graphql/api'
import { getGemstoneTypeName } from '@/domain/stone/search/nonsku/gemstone'

import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'

interface Props {
    modelValue?: NonSkuGemstoneType[] | null
    bounds: NonSkuGemstoneType[]
    title?: string
    isMulti?: boolean
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    isMulti: true,
    title: undefined,
})

const emit = defineEmits<{
    'update:modelValue': [NonSkuGemstoneType[]]
}>()

const isSelected = (value: NonSkuGemstoneType): boolean => {
    if (!props.modelValue) {
        return false
    }

    return props.modelValue?.includes(value)
}

const getClasses = (value: NonSkuGemstoneType): Record<string, boolean> => ({
    button: true,
    selected: isSelected(value),
})

const updateModelValue = (value: NonSkuGemstoneType[]) => {
    emit('update:modelValue', value)
}

const select = (value: NonSkuGemstoneType) => {
    if (props.isMulti) {
        if (!props.modelValue) {
            updateModelValue([value])
        } else if (props.modelValue.map((t) => t.id).includes(value.id)) {
            const model = props.modelValue.filter((v) => v !== value)

            updateModelValue(model)
        } else {
            const model = [...props.modelValue, value]

            updateModelValue(model)
        }
    } else {
        if (props.modelValue?.map((t) => t.id).includes(value.id)) {
            updateModelValue([])
        } else {
            updateModelValue([value])
        }
    }
}
</script>

<style lang="scss" scoped>
:deep(.button) {
    margin: 0 10px 10px 0;

    &.small {
        padding: 7px 25px;
    }

    &.solid {
        background: transparent;
        color: $slate;
        border-color: $grey-300;

        &:hover {
            background: $grey-200;
            color: $slate;
        }
    }

    &.selected {
        border-color: $slate;
    }
}
</style>
